import * as React from "react";
import { isUndefined, isEmpty } from 'lodash';

import Alert from './Alert';
import Field from '../Field';

import { ApiClient } from "../../../services/ApiClient";

import {IControlError, IControlSchema} from "../../Controls";
import { IDispatcher } from "../extensions/Dispatcher";
import { TextBoxValidate } from './validate';

import {clearNonNumberCharacters, Component, I18NString, } from "../../Utils";
import { clearSpaces, getFormatterNumber } from "../../../helpers/getFormatterNumber";

export interface ITextBoxSchema extends IControlSchema {
    extra: {
        hint: string;
    };
    description: I18NString;
    maxLength: number;
    disabled?: boolean;
    placeholder?: string;
    defaultValue?: string;
    isInteger?: boolean;
    lines?: number;
    value?: string;
    min?: number;
    max?: number;
    label?: string;
}

export interface ITextBoxProps {
    schema: ITextBoxSchema;
    api: ApiClient;
    dispatcher?: IDispatcher;
    error: IControlError | undefined;
    visited: boolean;
}

interface IState {
    lock: boolean;
    value: string;
    openInfo: boolean;
}

const Help = ({onClick}) =>  (
  <span onClick={onClick} className="input-group-addon btn btn-default">
        <span className="fa fa-question-circle-o fa-fw"></span>
    </span>
)

class TextBox extends Component<ITextBoxProps, IState> {
    private input: HTMLInputElement | HTMLTextAreaElement;

    state = {
        lock: false,
        value: '',
        openInfo: false
    };

    componentDidMount() {
        const { schema: { value, type }, error: existingError } = this.props;
        const isNumberType = type.toUpperCase() === "NUMBER";

        if (!isEmpty(value)) {
            this.props.dispatcher.onVisit(this.props.schema.id);
        }

        this.setState({ value: getFormatterNumber(value, isNumberType) })

        if (existingError) {
            return;
        }

        const error = TextBoxValidate(clearSpaces(value, isNumberType), this.props.schema);
        this.props.dispatcher.setError(
          this.props.schema.id,
          clearSpaces(this.input.value, isNumberType),
          error && error.isError ? { message: error.message } : undefined,
          false
        );
    }

    componentWillUpdate?(nextProps: Readonly<ITextBoxProps>, nextState: Readonly<IState>): void {
        const isNumberType = this.props.schema.type.toUpperCase() === "NUMBER";

        if (this.props.schema.value !== nextProps.schema.value && nextState.value !== nextProps.schema.value) {
            this.setState({ value: getFormatterNumber(nextProps.schema.value, isNumberType) });
        }
    }

    onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;

        this.setState({ value });
        this.throttledChange(value);
    }

    onNumberChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { schema } = this.props;
        const value = clearNonNumberCharacters(event.target.value, !!schema.isInteger);
        this.setState({ value });
        this.throttledChange(value);
    };

    throttledChange(value: string) {
        const { type } = this.props.schema;
        const isNumber = type.toUpperCase() === 'NUMBER';

        setTimeout(() => {
            if(value !== this.state.value) {
                return;
            }
            const error = TextBoxValidate(clearSpaces(value, isNumber), this.props.schema);
            this.props.dispatcher.onBlur(
              this.props.schema.id,
              clearSpaces(this.state.value, isNumber),
              error && error.isError,
              error && error.isError && { message: error.message }
            )
        }, 300);
    }

    onBlur = () => {
        if (!this.input) {
            return;
        }
        const { type } = this.props.schema;
        const isNumber = type.toUpperCase() === "NUMBER";

        let value = this.input.value;
        const error = TextBoxValidate(clearSpaces(value, isNumber), this.props.schema);

        this.props.dispatcher.onBlur(
          this.props.schema.id,
          clearSpaces(value, isNumber),
          !error,
          error && error.isError && { message: error.message }
        );
        this.props.dispatcher.onVisit(this.props.schema.id);

        this.setState({ value: getFormatterNumber(value, isNumber) })
    }
    onFocus = () => {
        const { type } = this.props.schema;
        const isNumber = type.toUpperCase() === 'NUMBER';
        this.input.value = clearSpaces(this.input.value, isNumber)
    }
    onClick = () => {
        this.setState(({openInfo}) => ({ openInfo: !openInfo }));
    }

    public render() {
        const { error, visited } = this.props;
        const { value } = this.state;

        const errorText = visited && error && error.message;
        let className = "default",
          input = null;
        // Error
        if (value && !errorText) {
            className = "success";
        } else if (errorText) {
            className = "error";
        }

        if (this.props.schema.type.toUpperCase() == "TEXTAREA") {
            let textarea = (
              <textarea
                style={{resize: "vertical"}}
                className="form-control"
                onChange={this.onChange}
                onBlur={this.onBlur}
                value={value}
                ref={input => this.input = input}
                rows={this.props.schema.lines}
                disabled={this.props.schema.disabled}
              />
            )

            input = !isUndefined(this.props.schema.description) ? (
              <div className="input-group" style={{width:"100%"}}>
                  {textarea}
                  <Help onClick={this.onClick}></Help>
              </div>
            ) : textarea;
        } else if (this.props.schema.type.toUpperCase() == "NUMBER") {
            input = (
              <div className="input-group" style={{width:"100%"}}>
                  <input
                    type="text"
                    className="form-control"
                    inputMode="decimal"
                    onChange={this.onNumberChange}
                    // onInput={this.onNumberInput}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                    value={value}
                    ref={input => this.input = input}
                    disabled={this.props.schema.disabled} />
                  <span className="input-group-addon">{this.props.schema.label}</span>
                  {(!isUndefined(this.props.schema.description)) && (
                    <Help onClick={this.onClick} />
                  )}
              </div>
            )}

        else {
            let textbox = (
              <input
                type="text"
                className="form-control"
                placeholder={this.props.schema.placeholder}
                onChange={this.onChange}
                maxLength={this.props.schema.maxLength + 1}
                onBlur={this.onBlur}
                value={value}
                title={value}
                disabled={this.props.schema.disabled}
                ref={input => this.input = input}
              />
            );

            input = !!this.props.schema.description ? (
              <div className="input-group" style={{width:"100%"}}>
                  {textbox}
                  <Help onClick={this.onClick} />
              </div>
            ) : textbox;
        }

        return (
          <Field {...this.props.schema} status={className} >
              {this.props.schema.extra && this.props.schema.extra.hint && (
                <div
                  className="alert alert-info hint"
                  dangerouslySetInnerHTML={{__html: this.props.schema.extra.hint }}
                />
              )}
              {input}
              {!isUndefined(this.props.schema.description) && this.state.openInfo && (
                <div
                  className="alert alert-warning"
                  role="alert"
                  dangerouslySetInnerHTML={{ __html: this.tr(this.props.schema.description)}}
                />
              )}
              {errorText && (
                <Alert>
                    {errorText}
                </Alert>
              )}
          </Field>
        );
    }
    //
    // private onNumberInput = (evt: React.ChangeEvent<HTMLInputElement>) => {
    //     const value = clearNonNumberCharacters(evt.target.value);
    //     this.setState({ ...this.state, value});
    // };

}

export function createTextBox(
  schema: IControlSchema,
  api: ApiClient,
  dispatcher: IDispatcher = null,
  error: IControlError | undefined,
  visited: boolean
): React.ReactElement<any> {
    return (
      <TextBox
        schema={schema as ITextBoxSchema}
        api={api}
        dispatcher={dispatcher}
        error={error}
        visited={visited}
      />
    );
}



// WEBPACK FOOTER //
// ./src/components/controls/standart/TextBox.tsx